import { useCallback, useEffect, useState } from 'react';
import { access, type Role } from '../api/access';
import {
  chart,
  type Headcount,
  type Position,
  type Reach,
  type SavePosition,
  type Scope,
  type SeatableUser,
} from '../api/chart';
import { organisation, type Department, type Location } from '../api/organisation';
import {
  IconCircleAlert,
  IconNetwork,
  IconPencil,
  IconPlus,
  IconRefreshCw,
  IconTrash2,
  IconUserPlus,
  IconUserMinus,
} from '../icons';
import { CODE_MAX, formatDate } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Drawer } from '../ui/Drawer';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, Select, Textarea, TextInput, Toggle } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';
import { RowMenu } from '../ui/RowMenu';
import { Table, type Column } from '../ui/Table';

/**
 * The organisation chart: seats, and who is sitting in them.
 *
 * A seat carries the authority and the reach, so a leaver does not take their region with
 * them and their successor does not have to be granted it again. A seat nobody holds is a
 * vacancy — shown rather than hidden, because counting seats is what tells the company how
 * many auditors it is meant to have against how many it has.
 */

interface Lists {
  departments: Department[];
  professions: { id: string; name: string }[];
  roles: Role[];
  operations: { id: string; name: string }[];
  regions: { id: string; name: string }[];
  locations: Location[];
  positions: Position[];
}

const blank: SavePosition = {
  code: '',
  title: '',
  departmentId: '',
  professionId: '',
  reportsToPositionId: null,
  description: null,
  isActive: true,
  roleIds: [],
  reach: [],
};

export function Chart() {
  const t = useTranslate();
  const [rows, setRows] = useState<Position[]>([]);
  const [headcount, setHeadcount] = useState<Headcount[]>([]);
  const [people, setPeople] = useState<SeatableUser[]>([]);
  const [lists, setLists] = useState<Lists | null>(null);

  const [draft, setDraft] = useState<{ id: string | null; values: SavePosition } | null>(null);
  // Seating an empty chair and handing a full one over are the same panel: pick a
  // person, pick a day. What differs is whether somebody is standing up at the same time.
  const [seating, setSeating] =
    useState<{ seat: Position; userId: string; from: string; devir: boolean } | null>(null);
  const [vacating, setVacating] = useState<Position | null>(null);
  const [seatError, setSeatError] = useState<string | null>(null);
  const [doomed, setDoomed] = useState<Position | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [seats, counts, seatable, departments, professions, roles, operations, regions, locations] =
        await Promise.all([
          chart.positions.list(),
          chart.headcount(),
          chart.seatable(),
          organisation.departments.list(),
          chart.professions.list(),
          access.roles.list(),
          organisation.operations.list(),
          organisation.regions.list(),
          organisation.locations.list({ activeOnly: 'true' }),
        ]);

      setRows(seats);
      setHeadcount(counts);
      setPeople(seatable);
      setLists({
        departments: departments.filter((d) => d.isActive),
        professions: professions.filter((p) => p.isActive).map((p) => ({ id: p.id, name: p.name })),
        roles: roles.filter((r) => r.isActive),
        operations: operations.map((o) => ({ id: o.id, name: o.name })),
        regions: regions.map((r) => ({ id: r.id, name: r.name })),
        locations,
        positions: seats,
      });
      setFailed(false);
      setError(null);
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const values = draft?.values;
  const set = (change: Partial<SavePosition>) =>
    setDraft((d) => (d ? { ...d, values: { ...d.values, ...change } } : d));

  function toggleRole(roleId: string) {
    setDraft((previous) => {
      if (!previous) return previous;

      const held = previous.values.roleIds.includes(roleId);

      return {
        ...previous,
        values: {
          ...previous.values,
          roleIds: held
            ? previous.values.roleIds.filter((r) => r !== roleId)
            : [...previous.values.roleIds, roleId],
        },
      };
    });
  }

  /**
   * Adds a piece of reach.
   *
   * Everywhere replaces the rest and nothing can be added beside it — the same rule the
   * server keeps, applied here so the screen does not offer a save it knows will be
   * refused. "Everywhere, and also the Aegean" is not a sentence anybody should read.
   */
  function addReach(scope: Scope, scopeId: string | null) {
    setDraft((previous) => {
      if (!previous) return previous;

      const next: Reach[] =
        scope === 'Global'
          ? [{ scope: 'Global', scopeId: null }]
          : previous.values.reach.some((r) => r.scope === 'Global')
            ? previous.values.reach
            : previous.values.reach.some((r) => r.scope === scope && r.scopeId === scopeId)
              ? previous.values.reach
              : [...previous.values.reach, { scope, scopeId }];

      return { ...previous, values: { ...previous.values, reach: next } };
    });
  }

  function removeReach(target: Reach) {
    setDraft((previous) =>
      previous
        ? {
            ...previous,
            values: {
              ...previous.values,
              reach: previous.values.reach.filter(
                (r) => !(r.scope === target.scope && r.scopeId === target.scopeId),
              ),
            },
          }
        : previous,
    );
  }

  async function save() {
    if (!draft || busy) return;

    setBusy(true);
    try {
      const body: SavePosition = {
        ...draft.values,
        code: draft.values.code.trim(),
        title: draft.values.title.trim(),
        description: draft.values.description?.trim() || null,
      };

      if (draft.id) await chart.positions.update(draft.id, body);
      else await chart.positions.create(body);

      setDraft(null);
      setPanelError(null);
      await reload();
    } catch (e) {
      setPanelError(describeError(e, 'error.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function seat() {
    if (!seating || busy) return;

    setBusy(true);
    try {
      const { seat: row, userId, from, devir } = seating;

      // One call either way. The handover ends the old spell and starts the new one in a
      // single save, so the seat is never empty at a moment anybody could read.
      if (devir) await chart.positions.handover(row.id, userId, from);
      else await chart.positions.seat(row.id, userId, from);

      setSeating(null);
      setSeatError(null);
      await reload();
    } catch (e) {
      // Into the panel, not the page behind it. A seating that is refused leaves the panel
      // open — the date is usually what needs changing — and a message on the page it is
      // covering reads as nothing having happened at all.
      setSeatError(describeError(e, 'error.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function vacate(row: Position) {
    if (!row.holder) return;

    setBusy(true);
    try {
      await chart.vacate(row.holder.id);
      setError(null);
      await reload();
    } catch (e) {
      setError(describeError(e, 'error.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: Position) {
    setBusy(true);
    try {
      await chart.positions.remove(row.id);
      setError(null);
      await reload();
    } catch (e) {
      setError(describeError(e, 'error.deleteFailed'));
    } finally {
      setBusy(false);
    }
  }

  const canSave = Boolean(
    values &&
      values.title.trim() &&
      values.departmentId &&
      values.professionId &&
      (draft?.id || values.code.trim()),
  );

  const reachLabel = (r: Reach) => {
    if (r.scope === 'Global') return t('chart.everywhere');
    if (r.name) return r.name;

    const named =
      lists?.operations.find((o) => o.id === r.scopeId)?.name ??
      lists?.regions.find((x) => x.id === r.scopeId)?.name ??
      lists?.locations.find((l) => l.id === r.scopeId)?.name;

    return named ?? t(`chart.scope.${r.scope}`);
  };

  const columns: Column<Position>[] = [
    {
      id: 'title',
      header: t('chart.seat'),
      className: 'is-primary',
      cell: (row) => (
        <>
          {row.title}
          <div className="nv-hint">
            <span className="nv-code">{row.code}</span> · {row.professionName}
          </div>
        </>
      ),
      skeletonWidth: '52%',
    },
    {
      id: 'department',
      header: t('chart.department'),
      cell: (row) => row.departmentName,
      skeletonWidth: '34%',
    },
    {
      id: 'holder',
      header: t('chart.holder'),
      cell: (row) =>
        row.holder ? (
          <>
            {row.holder.fullName}
            <div className="nv-hint">{t('chart.since', { date: formatDate(row.holder.from) })}</div>
          </>
        ) : (
          <Badge tone="na">{t('chart.vacant')}</Badge>
        ),
      skeletonWidth: '40%',
    },
    {
      id: 'reach',
      header: t('chart.reach'),
      cell: (row) =>
        row.reach.length === 0 ? (
          <span className="nv-subtle">{t('common.none')}</span>
        ) : (
          <span className="nv-badge-group">
            {row.reach.map((r) => (
              <Badge key={`${r.scope}:${r.scopeId}`} tone="neutral" shape="square">
                {reachLabel(r)}
              </Badge>
            ))}
          </span>
        ),
      skeletonWidth: '60%',
    },
    {
      id: 'reportsTo',
      header: t('chart.reportsTo'),
      cell: (row) =>
        row.reportsToTitle ?? <span className="nv-subtle">{t('chart.top')}</span>,
      skeletonWidth: '36%',
    },
  ];

  function open(row: Position) {
    setPanelError(null);
    setDraft({
      id: row.id,
      values: {
        code: row.code,
        title: row.title,
        departmentId: row.departmentId,
        professionId: row.professionId,
        reportsToPositionId: row.reportsToPositionId,
        description: row.description,
        isActive: row.isActive,
        roleIds: row.roleIds,
        reach: row.reach.map((r) => ({ scope: r.scope, scopeId: r.scopeId })),
      },
    });
  }

  function openNew() {
    setPanelError(null);
    setDraft({ id: null, values: { ...blank } });
  }

  /** Today, as the date input wants it. */
  const today = () => new Date().toISOString().slice(0, 10);

  function open_(row: Position, devir: boolean) {
    setSeatError(null);
    setSeating({ seat: row, userId: '', from: today(), devir });
  }

  const totals = headcount.reduce(
    (sum, row) => ({
      seats: sum.seats + row.seats,
      filled: sum.filled + row.filled,
      vacant: sum.vacant + row.vacant,
    }),
    { seats: 0, filled: 0, vacant: 0 },
  );

  return (
    <>
      <PageHeader
        crumbs={[t('app.people'), t('nav.chart')]}
        title={t('chart.title')}
        subtitle={t('chart.subtitle')}
        action={<Button variant="primary" icon={IconPlus} onClick={openNew}>{t('chart.add')}</Button>}
      />

      {error ? <Alert>{error}</Alert> : null}

      {/* The number the chart exists to make visible: seats against people in them. */}
      {headcount.length > 0 ? (
        <Card className="nv-pad">
          <h3 className="nv-h3">{t('chart.headcount')}</h3>
          <p className="nv-hint">{t('chart.headcountHint')}</p>

          <div className="nv-stat-row">
            {headcount.map((row) => (
              <div className="nv-stat" key={row.departmentId}>
                <span className="nv-stat__label">{row.departmentName}</span>
                <span className="nv-stat__value">
                  {row.filled} / {row.seats}
                </span>
                {row.vacant > 0 ? (
                  <Badge tone="na">{t('chart.vacantCount', { count: row.vacant })}</Badge>
                ) : null}
              </div>
            ))}

            <div className="nv-stat">
              <span className="nv-stat__label">{t('chart.total')}</span>
              <span className="nv-stat__value">
                {totals.filled} / {totals.seats}
              </span>
              {totals.vacant > 0 ? (
                <Badge tone="na">{t('chart.vacantCount', { count: totals.vacant })}</Badge>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}

      <Card className="nv-table-card">
        <Table
          caption={t('chart.title')}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : failed ? 'error' : 'ready'}
          onRowClick={open}
          rowActions={(row) => (
            <>
              <IconButton label={t('common.edit')} icon={IconPencil} onClick={() => open(row)} />
              <RowMenu
                label={t('common.moreActions')}
                items={[
                  ...(row.holder
                    ? [
                        {
                          label: t('chart.handover'),
                          icon: IconUserPlus,
                          disabled: busy || people.length === 0,
                          onSelect: () => open_(row, true),
                        },
                        {
                          label: t('chart.vacate'),
                          icon: IconUserMinus,
                          disabled: busy,
                          onSelect: () => setVacating(row),
                        },
                      ]
                    : [
                        {
                          label: t('chart.seatSomebody'),
                          icon: IconUserPlus,
                          disabled: busy || people.length === 0,
                          onSelect: () => open_(row, false),
                        },
                      ]),
                  {
                    label: row.holder ? t('chart.heldCannotDelete') : t('common.delete'),
                    icon: IconTrash2,
                    danger: true,
                    disabled: busy || row.holder !== null,
                    onSelect: () => setDoomed(row),
                  },
                ]}
              />
            </>
          )}
          errorState={
            <EmptyState
              icon={IconCircleAlert}
              tone="danger"
              title={t('common.loadFailed')}
              text={t('common.loadFailedText')}
              action={<Button icon={IconRefreshCw} onClick={() => void reload()}>{t('common.retry')}</Button>}
            />
          }
          emptyState={
            <EmptyState
              icon={IconNetwork}
              title={t('chart.empty')}
              text={t('chart.emptyText')}
              action={<Button variant="primary" icon={IconPlus} onClick={openNew}>{t('chart.add')}</Button>}
            />
          }
        />
      </Card>

      {/* ---------------------------------------------------------------- the seat */}

      <Drawer
        open={draft !== null}
        title={t(draft?.id ? 'chart.editTitle' : 'chart.newTitle')}
        onClose={() => setDraft(null)}
        footer={
          <>
            <Button onClick={() => setDraft(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!canSave || busy}
              loading={busy}
              loadingText={t('common.saving')}
              onClick={() => void save()}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {draft && values && lists ? (
          <>
            <Field label={t('chart.seatTitle')} required>
              {({ id }) => (
                <TextInput
                  id={id}
                  placeholder="Bölge Müdürü – Ege"
                  value={values.title}
                  onChange={(e) => set({ title: e.target.value })}
                />
              )}
            </Field>

            <Field label={t('common.code')} hint={t('chart.codeHint')}>
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  placeholder="bm-ege"
                  maxLength={CODE_MAX}
                  value={values.code}
                  onChange={(e) => set({ code: e.target.value })}
                />
              )}
            </Field>

            <Field label={t('chart.department')} required>
              {({ id }) => (
                <Select
                  id={id}
                  value={values.departmentId}
                  onChange={(e) => set({ departmentId: e.target.value })}
                >
                  <option value="">{t('common.none')}</option>
                  {lists.departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label={t('nav.professions')} required hint={t('chart.professionHint')}>
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={values.professionId}
                  onChange={(e) => set({ professionId: e.target.value })}
                >
                  <option value="">{t('common.none')}</option>
                  {lists.professions.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label={t('chart.reportsTo')} hint={t('chart.reportsToHint')}>
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={values.reportsToPositionId ?? ''}
                  onChange={(e) => set({ reportsToPositionId: e.target.value || null })}
                >
                  <option value="">{t('chart.top')}</option>
                  {lists.positions
                    .filter((p) => p.id !== draft.id)
                    .map((p) => (
                      <option key={p.id} value={p.id}>{p.title}</option>
                    ))}
                </Select>
              )}
            </Field>

            {/* ------------------------------------------------------------ reach */}

            <div className="nv-drawer__section">
              <h3 className="nv-h3">{t('chart.reach')}</h3>
              <p className="nv-hint">{t('chart.reachHint')}</p>

              {values.reach.length > 0 ? (
                <div className="nv-badge-group">
                  {values.reach.map((r) => (
                    <Badge key={`${r.scope}:${r.scopeId}`} tone="neutral" shape="square">
                      {reachLabel(r)}
                      <button
                        type="button"
                        className="nv-badge__remove"
                        aria-label={t('chart.removeReach', { name: reachLabel(r) })}
                        onClick={() => removeReach(r)}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="nv-hint">{t('chart.reachNone')}</p>
              )}

              {values.reach.some((r) => r.scope === 'Global') ? (
                <p className="nv-hint">{t('chart.reachGlobalHint')}</p>
              ) : (
                <>
                  <Field label={t('chart.addOperation')}>
                    {({ id }) => (
                      <Select
                        id={id}
                        value=""
                        onChange={(e) => e.target.value && addReach('Operation', e.target.value)}
                      >
                        <option value="">{t('common.none')}</option>
                        {lists.operations.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </Select>
                    )}
                  </Field>

                  <Field label={t('chart.addRegion')}>
                    {({ id }) => (
                      <Select
                        id={id}
                        value=""
                        onChange={(e) => e.target.value && addReach('Region', e.target.value)}
                      >
                        <option value="">{t('common.none')}</option>
                        {lists.regions.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </Select>
                    )}
                  </Field>

                  <Field label={t('chart.addLocation')}>
                    {({ id }) => (
                      <Select
                        id={id}
                        value=""
                        onChange={(e) => e.target.value && addReach('Location', e.target.value)}
                      >
                        <option value="">{t('common.none')}</option>
                        {lists.locations.map((l) => (
                          <option key={l.id} value={l.id}>{`${l.code} · ${l.name}`}</option>
                        ))}
                      </Select>
                    )}
                  </Field>

                  <Button icon={IconNetwork} onClick={() => addReach('Global', null)}>
                    {t('chart.addEverywhere')}
                  </Button>
                </>
              )}
            </div>

            {/* ------------------------------------------------------------ grants */}

            <div className="nv-drawer__section">
              <h3 className="nv-h3">{t('chart.grants')}</h3>
              <p className="nv-hint">{t('chart.grantsHint')}</p>

              <div className="nv-permission-list">
                {lists.roles.map((role) => (
                  <label className="nv-permission" key={role.id}>
                    <input
                      type="checkbox"
                      checked={values.roleIds.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                    />
                    <span>
                      {role.name}
                      {role.description ? <span className="nv-hint">{role.description}</span> : null}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <Field label={t('common.description')}>
              {({ id }) => (
                <Textarea
                  id={id}
                  placeholder={t('common.optional')}
                  value={values.description ?? ''}
                  onChange={(e) => set({ description: e.target.value || null })}
                />
              )}
            </Field>

            <div className="nv-drawer__section">
              <Toggle
                checked={values.isActive}
                onCheckedChange={(isActive) => set({ isActive })}
                label={t('common.active')}
              />
              <p className="nv-hint">{t('chart.isActiveHint')}</p>
            </div>

            {panelError ? <Alert>{panelError}</Alert> : null}
          </>
        ) : null}
      </Drawer>

      {/* ---------------------------------------------------------------- seating */}

      <Drawer
        open={seating !== null}
        title={t(seating?.devir ? 'chart.handover' : 'chart.seatSomebody')}
        onClose={() => setSeating(null)}
        footer={
          <>
            <Button onClick={() => setSeating(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!seating?.userId || !seating?.from || busy}
              loading={busy}
              loadingText={t('common.saving')}
              onClick={() => void seat()}
            >
              {t(seating?.devir ? 'chart.handoverConfirm' : 'chart.seatConfirm')}
            </Button>
          </>
        }
      >
        {seating ? (
          <>
            <p className="nv-hint">
              {seating.devir
                ? t('chart.handoverHint', {
                    title: seating.seat.title,
                    name: seating.seat.holder?.fullName ?? '',
                  })
                : t('chart.seatHint', { title: seating.seat.title })}
            </p>

            <Field label={t('chart.person')} required>
              {({ id }) => (
                <Select
                  id={id}
                  value={seating.userId}
                  onChange={(e) =>
                    setSeating((s) => (s ? { ...s, userId: e.target.value } : s))
                  }
                >
                  <option value="">{t('common.none')}</option>
                  {/* The free ones first: picking somebody who already holds a seat is allowed but
                      is rarely what was meant, and they were interleaved (field test, 2 November). */}
                  <optgroup label={t('chart.unseated')}>
                    {people.filter((p) => p.seats === 0).map((p) => (
                      <option key={p.id} value={p.id}>{p.fullName}</option>
                    ))}
                  </optgroup>
                  <optgroup label={t('chart.seatedElsewhere')}>
                    {people.filter((p) => p.seats > 0).map((p) => (
                      <option key={p.id} value={p.id}>
                        {`${p.fullName} (${t('chart.alreadySeated', { count: p.seats })})`}
                      </option>
                    ))}
                  </optgroup>
                </Select>
              )}
            </Field>

            <Field
              label={t(seating.devir ? 'chart.handoverFrom' : 'chart.from')}
              required
              hint={t(seating.devir ? 'chart.handoverFromHint' : 'chart.fromHint')}
            >
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="date"
                  value={seating.from}
                  onChange={(e) => setSeating((x) => (x ? { ...x, from: e.target.value } : x))}
                />
              )}
            </Field>

            <p className="nv-hint">{t('chart.seatExplains')}</p>

            {seatError ? <Alert>{seatError}</Alert> : null}
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={vacating !== null}
        title={vacating?.holder ? t('chart.vacateTitle', { name: vacating.holder.fullName }) : ''}
        confirmLabel={t('chart.vacate')}
        onConfirm={() => (vacating ? vacate(vacating) : Promise.resolve())}
        onClose={() => setVacating(null)}
      >
        <p>{t('chart.vacateWarning')}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={doomed !== null}
        title={doomed ? t('common.deleteTitle', { name: doomed.title }) : ''}
        confirmLabel={t('common.delete')}
        onConfirm={() => (doomed ? remove(doomed) : Promise.resolve())}
        onClose={() => setDoomed(null)}
      >
        <p>{t('common.deleteWarning')}</p>
      </ConfirmDialog>
    </>
  );
}
